import React, { useEffect, useState } from 'react';
import { Modal, Button, Input, TextArea, SearchableSelect } from '@/components/common';
import { useOrganizations } from '@/hooks/useOrganizations';
import type { CreateRoomData } from '@/types/room.types';
import { RoomApiError } from '@/services/room-api';
import { FaPlus } from 'react-icons/fa';

/** Sentinel for the "New organization…" entry, which no real id can collide with. */
const NEW_ORGANIZATION = '__new__';

interface CreateRoomModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateRoomData) => Promise<void>;
  isLoading: boolean;
}

interface FormErrors {
  name?: string;
  description?: string;
  maxPlayers?: string;
  organization?: string;
}

/**
 * Every island belongs to exactly one organization, chosen here and never
 * changed afterwards by its admins (§6.3). The dropdown offers the
 * organizations the user may create in; a user who administers none — the case
 * on a brand new account — only ever sees the new-organization field.
 */
export const CreateRoomModal: React.FC<CreateRoomModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading
}) => {
  const [formData, setFormData] = useState<CreateRoomData>({
    name: '',
    description: '',
    maxPlayers: 10
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>(NEW_ORGANIZATION);
  const [newOrganizationName, setNewOrganizationName] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // The list is only worth fetching while the form is open.
  const { organizations, isLoading: isLoadingOrganizations, error: organizationsError } =
    useOrganizations(isOpen);

  // Default to the first organization the user may create in; falling back to a
  // new one when there is none. Runs when the list arrives, not on every render.
  useEffect(() => {
    setSelectedOrganizationId(organizations.length > 0 ? organizations[0].id : NEW_ORGANIZATION);
  }, [organizations]);

  const isCreatingOrganization =
    selectedOrganizationId === NEW_ORGANIZATION || organizations.length === 0;

  const selectedOrganizationName =
    organizations.find(organization => organization.id === selectedOrganizationId)?.name ??
    'New organization…';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: name === 'maxPlayers' ? parseInt(value) || 1 : value 
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.name?.trim()) {
      newErrors.name = 'Island name is required';
    } else if (formData.name.trim().length > 86) {
      newErrors.name = 'Island name must be at most 86 characters';
    }
    
    if (formData.maxPlayers && (formData.maxPlayers < 1 || formData.maxPlayers > 100)) {
      newErrors.maxPlayers = 'Max players must be between 1 and 100';
    }

    if (isCreatingOrganization && !newOrganizationName.trim()) {
      newErrors.organization = 'Organization name is required';
    } else if (isCreatingOrganization && newOrganizationName.trim().length > 100) {
      newErrors.organization = 'Organization name must be at most 100 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', maxPlayers: 10 });
    setErrors({});
    setNewOrganizationName('');
    setSubmitError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || isLoading) return;

    setSubmitError(null);

    try {
      await onSubmit({
        ...formData,
        ...(isCreatingOrganization
          ? { newOrganizationName: newOrganizationName.trim() }
          : { organizationId: selectedOrganizationId })
      });
      resetForm();
      onClose();
    } catch (error) {
      // A refused organization is the one failure this form can explain and the
      // user can act on, so it is shown here rather than only by the parent.
      setSubmitError(describeSubmitFailure(error));
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create New Island">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Island Name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          error={errors.name}
          placeholder="Enter island name..."
          maxLength={86}
          required
          autoFocus
        />

        <TextArea
          label="Description (optional)"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          error={errors.description}
          placeholder="Enter island description..."
          rows={3}
        />

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Organization</label>

          {organizations.length > 0 && (
            // Searchable rather than a plain list: a user can administer more
            // organizations than a dropdown is worth scrolling (#64).
            <SearchableSelect
              ariaLabel="Organization"
              placeholder={selectedOrganizationName}
              emptyText="No organization matches that name"
              disabled={isLoading || isLoadingOrganizations}
              value={isCreatingOrganization ? null : selectedOrganizationId}
              options={organizations.map(organization => ({
                id: organization.id,
                label: organization.name,
                hint:
                  organization.activeIslandCount === 1
                    ? '1 island'
                    : `${organization.activeIslandCount} islands`
              }))}
              onSelect={setSelectedOrganizationId}
              action={{
                icon: <FaPlus />,
                label: 'New organization…',
                onSelect: () => setSelectedOrganizationId(NEW_ORGANIZATION)
              }}
            />
          )}

          {isCreatingOrganization && (
            <div className={organizations.length > 0 ? 'mt-2' : ''}>
              <Input
                label={organizations.length > 0 ? undefined : 'Name your organization'}
                name="newOrganizationName"
                value={newOrganizationName}
                onChange={e => {
                  setNewOrganizationName(e.target.value);
                  if (errors.organization) {
                    setErrors(prev => ({ ...prev, organization: undefined }));
                  }
                }}
                error={errors.organization}
                placeholder="Organization name..."
                maxLength={100}
              />
            </div>
          )}

          {organizationsError && (
            <p className="mt-1 text-sm text-game-accent">{organizationsError}</p>
          )}
        </div>

        <Input
          label="Max Players"
          name="maxPlayers"
          type="number"
          value={formData.maxPlayers?.toString() || '10'}
          onChange={handleInputChange}
          error={errors.maxPlayers}
          min="1"
          max="100"
        />

        {submitError && (
          <p className="text-sm text-game-accent" role="alert">
            {submitError}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isLoading}>
            Create Island
          </Button>
        </div>
      </form>
    </Modal>
  );
};

/**
 * The refusals the organization field can provoke, keyed by the server's
 * machine-readable `code`. Matching on the prose instead would lose this copy
 * the first time somebody rewords a backend message.
 */
const SUBMIT_FAILURES: Record<string, string> = {
  organization_name_taken: 'That organization name is already taken',
  forbidden:
    'You cannot create an island in that organization. Ask one of its admins, or name a new one.',
  organization_not_found: 'That organization no longer exists. Pick another, or name a new one.'
};

/**
 * Turn a create failure into something the user can act on: our copy when the
 * error carries a code we recognise, otherwise whatever the server said.
 */
function describeSubmitFailure(error: unknown): string {
  const code = error instanceof RoomApiError ? error.code : undefined;
  if (code && SUBMIT_FAILURES[code]) {
    return SUBMIT_FAILURES[code];
  }

  return (error instanceof Error && error.message) || 'Failed to create island';
}
